import type { BubbleRole, EffectiveSkin, SkinComponentOverride, SkinTokens } from './types';

const FONT_STYLE_ID = 'zxtalk-skin-fonts';
const COMPONENT_STYLE_ID = 'zxtalk-skin-components';

export function normalizeHex(value: string): string {
  const trimmed = value.trim();
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    return `#${trimmed.slice(1).split('').map((part) => part + part).join('')}`.toLowerCase();
  }
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.toLowerCase();
  return '#000000';
}

export function hexToRgb(value: string): [number, number, number] {
  const hex = normalizeHex(value).slice(1);
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ];
}

export function rgba(value: string, alpha: number): string {
  const [r, g, b] = hexToRgb(value);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha)).toFixed(3)})`;
}

/** Blend two colors in sRGB. Used only for validated skin colors. */
export function mixHex(base: string, tint: string, strength: number): string {
  const [br, bg, bb] = hexToRgb(base);
  const [tr, tg, tb] = hexToRgb(tint);
  const ratio = Math.max(0, Math.min(1, strength));
  const channel = (from: number, to: number): string =>
    Math.round(from + (to - from) * ratio).toString(16).padStart(2, '0');
  return `#${channel(br, tr)}${channel(bg, tg)}${channel(bb, tb)}`;
}

/** Pressed colors stay legible even when the chosen theme color is very dark. */
export function pressedColor(theme: string): string {
  const [r, g, b] = hexToRgb(theme);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return mixHex(theme, luminance < 0.22 ? '#ffffff' : '#000000', 0.2);
}

export function readableTextColor(value: string, darkTextThreshold = 0.45): '#0b1117' | '#ffffff' {
  const [r, g, b] = hexToRgb(value).map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > darkTextThreshold ? '#0b1117' : '#ffffff';
}

export function skinCssVariables(tokens: SkinTokens): Record<string, string> {
  const panelAlpha = 1 - tokens.glass.panelTransparency / 100;
  const controlAlpha = 1 - tokens.glass.controlTransparency / 100;
  const overlayAlpha = 1 - tokens.glass.overlayTransparency / 100;
  const themeColor = tokens.palette.primary;
  const tintColor = tokens.glass.tintColor ?? themeColor;
  const tintStrength = Math.max(0, Math.min(0.6, (tokens.glass.tintStrength ?? 18) / 100));
  const panelColor = tokens.glass.color ?? mixHex(tokens.palette.panel, tintColor, tintStrength);
  const controlColor = tokens.glass.color ?? mixHex(tokens.palette.control, tintColor, tintStrength);
  const overlayColor = tokens.glass.color ?? mixHex(tokens.palette.overlay, tintColor, tintStrength);
  const pageColor = tokens.glass.color ?? mixHex(tokens.palette.background, tintColor, tintStrength * 0.55);
  const textColor = tokens.glass.color
    ? readableTextColor(mixHex(tokens.palette.background, pageColor, panelAlpha), 0.19)
    : tokens.palette.text;
  const iconColor = tokens.icons.followTheme === false ? tokens.icons.color : themeColor;
  const activeBackground = tokens.icons.followTheme === false
    ? tokens.icons.activeBackground
    : rgba(themeColor, 0.22);
  return {
    '--color-bg': 'transparent',
    '--color-surface': rgba(panelColor, panelAlpha),
    '--color-surface-2': rgba(controlColor, controlAlpha),
    '--color-primary': themeColor,
    '--color-primary-pressed': tokens.palette.primaryPressed,
    '--color-on-primary': readableTextColor(themeColor),
    '--color-text': textColor,
    '--color-text-secondary': tokens.glass.color ? rgba(textColor, 0.72) : tokens.palette.textSecondary,
    '--color-border': rgba(tokens.glass.color ? textColor : tokens.palette.border, tokens.glass.color ? 0.22 : Math.max(0.18, panelAlpha * 0.65)),
    '--color-danger': tokens.palette.danger,
    '--color-on-danger': readableTextColor(tokens.palette.danger),
    '--color-overlay': tokens.palette.backdrop,
    '--color-icon': iconColor,
    '--skin-background': tokens.palette.background,
    '--skin-wallpaper-text': tokens.glass.color ? readableTextColor(tokens.palette.background, 0.19) : tokens.palette.text,
    '--skin-wallpaper-text-secondary': tokens.glass.color ? rgba(readableTextColor(tokens.palette.background, 0.19), 0.72) : tokens.palette.textSecondary,
    '--skin-background-gradient': tokens.palette.backgroundGradient ?? 'none',
    '--skin-panel': rgba(panelColor, panelAlpha),
    '--skin-control': rgba(controlColor, controlAlpha),
    '--skin-overlay': rgba(overlayColor, overlayAlpha),
    '--skin-page': rgba(pageColor, panelAlpha),
    '--skin-backdrop': tokens.palette.backdrop,
    '--skin-blur': `${tokens.glass.blurPx}px`,
    '--skin-saturation': `${tokens.glass.saturation}%`,
    '--skin-panel-alpha': String(panelAlpha),
    '--skin-control-alpha': String(controlAlpha),
    '--skin-overlay-alpha': String(overlayAlpha),
    '--skin-icon-stroke': String(tokens.icons.strokeWidth),
    '--skin-icon-active': activeBackground,
    '--skin-glass-tint': tintColor,
    '--skin-glass-tint-strength': `${String(Math.round(tintStrength * 100))}%`,
    '--radius-xs': `${tokens.radii.xs}px`,
    '--radius-sm': `${tokens.radii.sm}px`,
    '--radius-md': `${tokens.radii.md}px`,
    '--radius-lg': `${tokens.radii.lg}px`,
    '--radius-full': `${tokens.radii.full}px`,
    '--font-xs': `${tokens.typography.xs}px`,
    '--font-sm': `${tokens.typography.sm}px`,
    '--font-md': `${tokens.typography.md}px`,
    '--font-lg': `${tokens.typography.lg}px`,
    '--font-xl': `${tokens.typography.xl}px`,
    '--font-body': tokens.typography.bodyFamily,
    '--font-heading': tokens.typography.headingFamily,
    '--shadow-1': tokens.shadows.low,
    '--shadow-2': tokens.shadows.high,
    '--bubble-user': rgba(tokens.bubbles.user, Math.max(0.58, panelAlpha)),
    '--bubble-user-text': readableTextColor(tokens.bubbles.user),
    '--bubble-assistant': rgba(tokens.bubbles.assistant, Math.max(0.58, controlAlpha)),
    '--bubble-assistant-text': readableTextColor(tokens.bubbles.assistant),
    '--bubble-system': rgba(tokens.bubbles.system, Math.max(0.58, panelAlpha)),
    '--bubble-system-text': readableTextColor(tokens.bubbles.system),
    '--skin-wallpaper-fit': tokens.wallpaper.fit,
    '--skin-wallpaper-position': tokens.wallpaper.position,
    '--skin-wallpaper-image': 'none',
    ...bubbleImageVariables(tokens),
  };
}

const BUBBLE_ROLES: BubbleRole[] = ['user', 'assistant', 'system'];

function bubbleImageVariables(tokens: SkinTokens): Record<string, string> {
  const variables: Record<string, string> = {};
  for (const role of BUBBLE_ROLES) {
    const image = tokens.bubbleImages?.[role];
    variables[`--bubble-${role}-image`] = 'none';
    variables[`--bubble-${role}-slice`] = image
      ? `${String(image.sliceTop)} ${String(image.sliceRight)} ${String(image.sliceBottom)} ${String(image.sliceLeft)} fill`
      : '0';
    variables[`--bubble-${role}-width`] = `${String(image?.width ?? 0)}px`;
    variables[`--bubble-${role}-repeat`] = image?.repeat ?? 'stretch';
    variables[`--bubble-${role}-padding`] = image
      ? `${String(image.paddingTop)}px ${String(image.paddingRight)}px ${String(image.paddingBottom)}px ${String(image.paddingLeft)}px`
      : '8px 12px';
  }
  return variables;
}

export function componentOverrideCss(
  overrides: Record<string, SkinComponentOverride>,
  rootSelector = '',
): string {
  return Object.entries(overrides).map(([componentId, override]) => {
    const selector = `${rootSelector}[data-skin-id="${componentId.replace(/["\\]/g, '')}"]`;
    const declarations: string[] = [];
    const style = override.style;
    if (style?.background !== undefined) declarations.push(`background:${style.background} !important`);
    if (style?.color !== undefined) declarations.push(`color:${style.color} !important`);
    if (style?.borderColor !== undefined) declarations.push(`border-color:${style.borderColor} !important`);
    if (style?.borderWidth !== undefined) declarations.push(`border-width:${String(style.borderWidth)}px !important`);
    if (style?.borderStyle !== undefined) declarations.push(`border-style:${style.borderStyle} !important`);
    if (style?.borderRadius !== undefined) declarations.push(`border-radius:${style.borderRadius}px !important`);
    if (style?.paddingX !== undefined) declarations.push(`padding-left:${String(style.paddingX)}px !important`);
    if (style?.paddingX !== undefined) declarations.push(`padding-right:${String(style.paddingX)}px !important`);
    if (style?.paddingY !== undefined) declarations.push(`padding-top:${String(style.paddingY)}px !important`);
    if (style?.paddingY !== undefined) declarations.push(`padding-bottom:${String(style.paddingY)}px !important`);
    if (style?.gap !== undefined) declarations.push(`gap:${String(style.gap)}px !important`);
    if (style?.boxShadow !== undefined) declarations.push(`box-shadow:${style.boxShadow} !important`);
    if (style?.opacity !== undefined) declarations.push(`opacity:${style.opacity} !important`);
    if (style?.blur !== undefined) declarations.push(`backdrop-filter:blur(${style.blur}px) saturate(var(--skin-saturation)) !important`);
    if (style?.saturation !== undefined) declarations.push(`backdrop-filter:blur(var(--skin-blur)) saturate(${String(style.saturation)}%) !important`);
    if (style?.fontFamily !== undefined) declarations.push(`font-family:${style.fontFamily}, var(--font-body) !important`);
    if (style?.fontSize !== undefined) declarations.push(`font-size:${style.fontSize}px !important`);
    if (style?.fontWeight !== undefined) declarations.push(`font-weight:${style.fontWeight} !important`);
    if (style?.lineHeight !== undefined) declarations.push(`line-height:${String(style.lineHeight)} !important`);
    if (style?.letterSpacing !== undefined) declarations.push(`letter-spacing:${String(style.letterSpacing)}px !important`);
    if (style?.textAlign !== undefined) declarations.push(`text-align:${style.textAlign} !important`);
    if (style?.iconColor !== undefined) declarations.push(`--color-icon:${style.iconColor} !important`);
    if (style?.iconSize !== undefined) declarations.push(`--skin-icon-size:${String(style.iconSize)}px !important`);
    if (override.hidden === true) declarations.push('display:none !important');
    return declarations.length === 0 ? '' : `${selector}{${declarations.join(';')}}`;
  }).join('\n');
}

export function applySkinToDocument(skin: EffectiveSkin): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const variables = skinCssVariables(skin.tokens);
  for (const [key, value] of Object.entries(variables)) root.style.setProperty(key, value);
  root.dataset.skinTheme = skin.tokens.theme;
  root.dataset.skinSource = skin.source;
  root.dataset.skinOffline = skin.offline ? 'true' : 'false';
  root.style.colorScheme = skin.tokens.theme;

  if (skin.wallpaperDataUrl) root.style.setProperty('--skin-wallpaper-image', `url("${skin.wallpaperDataUrl}")`);
  for (const role of BUBBLE_ROLES) {
    const source = skin.bubbleSources?.[role];
    root.style.setProperty(
      `--bubble-${role}-image`,
      source ? `url("${source.replace(/"/g, '%22')}")` : 'none',
    );
  }

  let componentStyle = document.getElementById(COMPONENT_STYLE_ID) as HTMLStyleElement | null;
  if (componentStyle === null) {
    componentStyle = document.createElement('style');
    componentStyle.id = COMPONENT_STYLE_ID;
    document.head.append(componentStyle);
  }
  componentStyle.textContent = componentOverrideCss(skin.tokens.componentOverrides);
}

export function applyFonts(tokens: SkinTokens, assets: Record<string, string>, fontAssets: { id: string; slot: string; mime: string }[]): void {
  if (typeof document === 'undefined') return;
  let style = document.getElementById(FONT_STYLE_ID) as HTMLStyleElement | null;
  if (style === null) {
    style = document.createElement('style');
    style.id = FONT_STYLE_ID;
    document.head.append(style);
  }
  const families = new Map<string, string>();
  const rules: string[] = [];
  for (const asset of fontAssets) {
    const dataUrl = assets[asset.id];
    const family = `ZxSkin-${asset.slot}-${asset.id.slice(0, 8)}`;
    if (!dataUrl) continue;
    families.set(asset.slot, family);
    rules.push(`@font-face{font-family:"${family}";src:url("${dataUrl}") format("woff2");font-display:swap;}`);
  }
  style.textContent = rules.join('\n');
  const body = families.get('body') ?? tokens.typography.bodyFamily;
  const heading = families.get('heading') ?? tokens.typography.headingFamily;
  document.documentElement.style.setProperty('--font-body', `"${body}", system-ui, sans-serif`);
  document.documentElement.style.setProperty('--font-heading', `"${heading}", system-ui, sans-serif`);
}

export function applyTransientComponentStyle(styleId: string, overrides: Record<string, SkinComponentOverride>): void {
  if (typeof document === 'undefined') return;
  let style = document.getElementById(styleId) as HTMLStyleElement | null;
  if (style === null) {
    style = document.createElement('style');
    style.id = styleId;
    document.head.append(style);
  }
  style.textContent = componentOverrideCss(overrides);
}

export function removeTransientStyle(styleId: string): void {
  if (typeof document === 'undefined') return;
  document.getElementById(styleId)?.remove();
}

